/**
 * The queue that carries a student's answers to the server.
 *
 * Answers appear on screen the moment they are chosen and are sent separately,
 * so a slow or broken connection never blocks the paper. Each change carries
 * the time the student made it: the server discards a save that arrives late
 * but was made earlier, which is what stops a retry reverting a newer answer.
 */

export interface PendingAnswer {
  questionId: string;
  selectedAnswer: string[];
  /** When the student made this choice, by this device's clock. */
  answeredAt: string;
}

export type SaveStatus = 'saved' | 'saving' | 'pending' | 'error';

/**
 * Adds a change to the queue.
 *
 * Only the latest choice for a question matters, so a student changing their
 * mind five times sends one answer rather than five.
 */
export const enqueue = (queue: PendingAnswer[], answer: PendingAnswer): PendingAnswer[] => [
  ...queue.filter((pending) => pending.questionId !== answer.questionId),
  answer,
];

/** The body one save call takes. */
export const toBatch = (queue: PendingAnswer[]) => ({
  answers: queue.map((pending) => ({
    questionId: pending.questionId,
    selectedAnswer: pending.selectedAnswer,
    answeredAt: pending.answeredAt,
  })),
});

/**
 * What is left to send after a batch was accepted.
 *
 * Anything the student changed while the batch was in flight stays queued, and
 * a question whose newer answer arrived meanwhile is not overwritten by the one
 * that was just sent.
 */
export const settle = (queue: PendingAnswer[], sent: PendingAnswer[]): PendingAnswer[] => {
  const sentAt = new Map(sent.map((pending) => [pending.questionId, pending.answeredAt]));

  return queue.filter((pending) => {
    const sentTime = sentAt.get(pending.questionId);
    if (sentTime === undefined) {
      return true;
    }
    // Still queued only if it changed again after the version that was sent.
    return pending.answeredAt > sentTime;
  });
};

/** How long to wait before trying again, backing off but never giving up slowly. */
export const backoffMs = (failures: number): number => {
  const capped = Math.min(Math.max(failures, 1), 6);
  return Math.min(1000 * 2 ** (capped - 1), 30000);
};

export const statusOf = (queue: PendingAnswer[], inFlight: boolean, failures: number): SaveStatus => {
  if (failures > 0 && queue.length > 0) {
    return 'error';
  }
  if (inFlight) {
    return 'saving';
  }
  return queue.length > 0 ? 'pending' : 'saved';
};

/** What the student is told about their answers being stored. */
export const statusLabel = (status: SaveStatus, pending: number): string => {
  switch (status) {
    case 'saving':
      return 'Saving your answers';
    case 'pending':
      return `${pending} answer${pending === 1 ? '' : 's'} waiting to save`;
    case 'error':
      return 'Cannot reach the server. Your answers are safe here and will be sent when it returns.';
    default:
      return 'All answers saved';
  }
};
